import type { Font } from '../value-objects/font'

/**
 * Font Display Utilities
 * Handles font naming and display logic for variations
 */

/**
 * Get display name for a font variation
 * Avoids duplicating weight/style information already in the name
 */
export const getFontDisplayName = (font: Font): string => {
  const baseName = font.name
  const nameLower = baseName.toLowerCase()

  // Check if the name already contains weight/style descriptors
  const hasWeightInName =
    nameLower.includes('bold') ||
    nameLower.includes('light') ||
    nameLower.includes('thin') ||
    nameLower.includes('medium') ||
    nameLower.includes('black')

  const hasItalicInName = nameLower.includes('italic') || nameLower.includes('oblique')

  // Build display name parts
  const parts: string[] = [baseName]

  // Add weight descriptor if not already in name
  if (!hasWeightInName && font.weight !== 400) {
    if (font.weight === 700) parts.push('Bold')
    else if (font.weight === 300) parts.push('Light')
    else if (font.weight === 100) parts.push('Thin')
    else if (font.weight === 500) parts.push('Medium')
    else if (font.weight === 900) parts.push('Black')
    // Add more weight mappings as needed
  }

  // Add italic descriptor if not already in name
  if (!hasItalicInName && font.italic) {
    parts.push('Italic')
  }

  return parts.join(' ')
}

/**
 * Get the base font family name (without weight/style descriptors)
 * This is used for grouping fonts
 */
export const getBaseFontFamily = (fontName: string): string => {
  // Remove common weight/style descriptors from the end of the name
  return fontName
    .replace(/\s*(Bold|Light|Thin|Medium|Black|Regular|Italic|Oblique)\s*$/i, '')
    .trim()
}

/**
 * Sort fonts by name and then by weight/italic
 */
export const sortFonts = (fonts: Font[]): Font[] => {
  return [...fonts].sort((a, b) => {
    // First sort by base family name
    const baseA = getBaseFontFamily(a.name)
    const baseB = getBaseFontFamily(b.name)

    if (baseA !== baseB) {
      return baseA.localeCompare(baseB)
    }

    // Then sort by weight (lighter first)
    if (a.weight !== b.weight) {
      return a.weight - b.weight
    }

    // Finally sort by italic (non-italic first)
    if (a.italic !== b.italic) {
      return a.italic ? 1 : -1
    }

    return 0
  })
}

/**
 * Group fonts by family for potential future use
 */
export type FontFamily = {
  family: string
  variations: Font[]
}

export const groupFontsByFamily = (fonts: Font[]): FontFamily[] => {
  const families = new Map<string, Font[]>()

  fonts.forEach((font) => {
    const family = getBaseFontFamily(font.name)
    const existing = families.get(family) || []
    families.set(family, [...existing, font])
  })

  return Array.from(families.entries()).map(([family, variations]) => ({
    family,
    variations: variations.sort((a, b) => {
      if (a.weight !== b.weight) return a.weight - b.weight
      if (a.italic !== b.italic) return a.italic ? 1 : -1
      return 0
    }),
  }))
}
